import { useEffect, useRef, useState } from 'react';
import { GlassPanel } from '@/components/GlassPanel';
import { KpiStrip } from '@/components/KpiStrip';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

// SVG Slovenia map – real OSM border (RDP simplified, 120 pts) + population-scaled city dots
const SloveniaMap = ({ animate, regionWest, regionEast }) => {
  const cities = [
    { cx:105.7, cy:110.8, r:7.50, label:'Ljubljana' },
    { cx:211.8, cy: 42.5, r:4.92, label:'Maribor' },
    { cx: 91.7, cy: 85.4, r:4.10, label:'Kranj' },
    { cx: 33.0, cy:177.9, r:4.03, label:'Koper' },
    { cx:175.5, cy: 86.7, r:3.95, label:'Celje' },
    { cx:167.1, cy:144.4, r:3.63, label:'Novo Mesto' },
    { cx: 25.6, cy:108.2, r:3.45, label:'Nova Gorica' },
    { cx:196.9, cy:122.9, r:3.29, label:'Krško' },
    { cx:161.5, cy: 69.3, r:3.29, label:'Velenje' },
    { cx:232.3, cy: 61.2, r:3.18, label:'Ptuj' },
    { cx:260.3, cy: 29.0, r:3.03, label:'Murska Sobota' },
    { cx:204.4, cy: 65.2, r:2.95, label:'Sl. Bistrica' },
    { cx:158.7, cy: 49.2, r:2.90, label:'Slovenj Gradec' },
    { cx:156.0, cy: 97.4, r:2.90, label:'Trbovlje' },
    { cx:138.3, cy:165.8, r:2.86, label:'Kočevje' },
    { cx: 86.1, cy: 94.7, r:2.71, label:'Škofja Loka' },
    { cx: 33.0, cy: 92.1, r:2.71, label:'Tolmin' },
    { cx: 77.7, cy:147.0, r:2.55, label:'Postojna' },
    { cx: 68.4, cy: 67.9, r:2.49, label:'Bled' },
    { cx: 37.7, cy: 51.8, r:2.28, label:'Kranjska Gora' },
  ];

  // Real OSM border – viewBox 0 0 300 195.1, aspect ratio 1.538 (geographically correct)
  const borderPath = "M0.0,77.6 L4.4,86.1 L3.3,89.1 L14.3,88.2 L17.8,92.6 L26.9,93.7 L25.0,99.1 L11.3,109.7 L12.4,112.9 L9.4,116.7 L14.4,122.1 L24.9,119.8 L18.6,137.2 L20.5,143.3 L39.0,151.8 L50.6,166.7 L43.2,173.5 L23.6,166.9 L0.8,175.7 L27.9,192.3 L38.3,189.1 L40.9,193.4 L47.7,194.5 L50.2,190.8 L56.8,190.5 L54.6,183.8 L58.3,181.9 L70.9,187.9 L77.5,188.7 L80.3,183.8 L88.2,188.5 L93.5,186.2 L103.9,179.5 L104.9,170.1 L108.0,169.3 L111.3,161.4 L114.8,162.6 L113.6,167.5 L122.5,173.8 L122.5,181.0 L132.3,184.2 L134.3,190.0 L142.8,187.6 L144.9,180.6 L159.3,186.8 L167.8,195.1 L187.2,186.4 L179.4,179.8 L177.1,170.6 L189.1,164.5 L185.7,158.6 L183.7,161.6 L184.6,155.9 L181.4,161.3 L178.1,158.8 L175.2,155.2 L176.9,152.4 L195.1,145.0 L194.7,141.9 L200.5,141.2 L201.5,138.0 L211.1,141.6 L217.4,138.1 L214.4,135.1 L214.6,130.3 L217.2,128.0 L216.5,117.9 L219.5,111.6 L209.1,105.5 L206.8,97.9 L211.6,88.4 L222.8,89.5 L225.0,82.8 L245.8,76.1 L251.6,71.2 L249.8,64.8 L272.5,66.8 L266.9,50.7 L278.7,43.1 L293.0,53.4 L300.0,53.7 L293.2,49.8 L291.8,41.8 L280.9,32.6 L284.3,24.5 L278.8,23.1 L274.1,16.4 L273.6,10.6 L276.8,4.8 L266.1,0.0 L243.1,6.6 L243.3,18.2 L247.2,22.1 L248.0,29.6 L229.5,20.7 L206.7,25.0 L198.1,35.2 L189.9,29.6 L155.1,30.2 L149.4,36.9 L147.2,32.7 L143.8,36.8 L139.2,36.1 L134.4,49.2 L124.7,50.5 L123.3,55.4 L113.7,59.0 L110.7,67.6 L106.8,60.3 L102.8,62.1 L98.2,57.6 L73.4,59.4 L66.7,52.4 L31.5,47.4 L29.2,58.5 L20.7,58.6 L17.7,64.2 L5.9,69.3 L6.8,73.1 Z";

  return (
    <svg viewBox="0 0 300 195.1" className="w-full h-auto">
      <defs>
        <filter id="glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="3.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <clipPath id="slo-clip">
          <path d={borderPath} />
        </clipPath>
        {/* West region gradient */}
        <linearGradient id="westGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#C87533" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#C87533" stopOpacity="0.04" />
        </linearGradient>
        {/* East region gradient */}
        <linearGradient id="eastGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#38BDF8" stopOpacity="0.10" />
        </linearGradient>
      </defs>

      {/* Region fills – clipped to Slovenia border */}
      <rect x="0" y="0" width="130" height="195.1" fill="url(#westGrad)" clipPath="url(#slo-clip)" />
      <rect x="130" y="0" width="170" height="195.1" fill="url(#eastGrad)" clipPath="url(#slo-clip)" />

      {/* Real OSM border */}
      <path
        d={borderPath}
        fill="none"
        stroke="rgba(255,255,255,0.18)"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />

      {/* Region divider */}
      <line x1="130" y1="0" x2="130" y2="195.1" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8" strokeDasharray="4 3" clipPath="url(#slo-clip)" />

      {/* Region labels */}
      <text x="48" y="28" textAnchor="middle" fontFamily="monospace" fontSize="7.5" fill="rgba(200,117,51,0.55)" letterSpacing="1.5">{regionWest}</text>
      <text x="248" y="148" textAnchor="middle" fontFamily="monospace" fontSize="7.5" fill="rgba(56,189,248,0.55)" letterSpacing="1.5">{regionEast}</text>

      {/* Population-scaled city dots with edge glow */}
      {cities.map((dot, i) => {
        const isEast = dot.cx >= 130;
        const dur  = (5 + Math.random() * 5).toFixed(2);
        const del  = (Math.random() * 5).toFixed(2);
        return (
        <g key={i}>
          {/* Pulsing border ring only */}
          {animate && (
            <circle
              cx={dot.cx} cy={dot.cy} r={dot.r + 3}
              fill="none"
              stroke={isEast ? '#38BDF8' : '#FF8C2A'}
              strokeWidth="1.5"
              filter="url(#glow)"
              className="ring-pulse"
              style={{ '--glow-dur': `${dur}s`, '--glow-delay': `${del}s` }}
            />
          )}
          {/* Static main dot */}
          <circle
            cx={dot.cx} cy={dot.cy} r={dot.r}
            fill={isEast ? '#38BDF8' : '#C87533'}
            opacity={animate ? 0.9 : 0.25}
            filter={animate ? 'url(#glow)' : undefined}
            className={animate ? 'map-dot' : ''}
            style={{ animationDelay: `${i * 0.08}s` }}
          />
        </g>
        );
      })}
      ))}
    </svg>
  );
};

export const LegacySection = () => {
  const sectionRef = useRef(null);
  const [mapAnimated, setMapAnimated] = useState(false);
  const { lang } = useLanguage();
  const tx = t(lang).legacy;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMapAnimated(true);
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="legacy"
      ref={sectionRef}
      data-testid="legacy-section"
      className="scroll-section min-h-screen flex items-center px-4"
    >
      <div className="section-content max-w-[1200px] mx-auto">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-[#C87533]" />
          <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.label}</span>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 items-center">
          {/* Left: Content */}
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-3 text-white">
              {tx.headline}
            </h2>
            <p className="text-white/55 text-sm lg:text-base leading-relaxed mb-8">
              {tx.body}
            </p>

            <div className="space-y-5 mb-8">
              <div className="flex gap-4">
                <div className="w-1 bg-[#C87533]/50 rounded-full flex-shrink-0" />
                <div>
                  <h3 className="font-display text-base font-semibold text-white mb-1">{tx.feat1Title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{tx.feat1Body}</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-1 bg-[#C87533]/50 rounded-full flex-shrink-0" />
                <div>
                  <h3 className="font-display text-base font-semibold text-white mb-1">{tx.feat2Title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{tx.feat2Body}</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-1 bg-[#C87533]/50 rounded-full flex-shrink-0" />
                <div>
                  <h3 className="font-display text-base font-semibold text-white mb-1">{tx.feat3Title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{tx.feat3Body}</p>
                </div>
              </div>
            </div>

            <KpiStrip
              kpis={[
                { value: 1500, suffix: '+', label: tx.kpi1 },
                { value: 2800, suffix: '+', label: tx.kpi2 },
                { staticValue: '★★★★★', label: tx.kpi3 },
                { value: 21, suffix: '+', label: tx.kpi4 },
              ]}
            />
          </div>

          {/* Right: Map visual */}
          <div>
            <GlassPanel className="p-6 sm:p-8">
              <SloveniaMap animate={mapAnimated} regionWest={tx.regionWest} regionEast={tx.regionEast} />
            </GlassPanel>
          </div>
        </div>
      </div>
    </section>
  );
};
